/**
 * จุดเดียวที่ตัดสินใจว่าแอปจะยิง API ไปที่ไหน
 *
 * ลำดับความสำคัญตอนรัน:
 *   1. NUXT_PUBLIC_API_BASE ใน .env (หรือ env ของ CI ตอน build)
 *   2. DEFAULT_API_BASE ด้านล่าง
 *
 * ค่านี้ถูกอ่านที่ nuxt.config.ts แล้วส่งเข้า runtimeConfig.public.apiBase
 * โค้ดทุกที่ต้องเรียกผ่าน useBaseUrl() ห้าม import ค่านี้ไปใช้ตรง ๆ
 *
 * หมายเหตุ: โปรเจกต์นี้ deploy ด้วย `nuxt generate` (static) ค่าจึงถูกฝัง
 * ตั้งแต่ตอน build — ต้องตั้ง env ให้ถูกก่อนสั่ง build ไม่ใช่ตอน serve
 */
export const API_ENDPOINTS = {
  prod: "https://99pd3xjl10.execute-api.ap-southeast-1.amazonaws.com",
  dev: "https://b8f74o2sda.execute-api.ap-southeast-1.amazonaws.com",
  beta: "https://ixkk2e07of.execute-api.ap-southeast-1.amazonaws.com",
} as const;

/** ไม่ตั้ง env = ยิง production เสมอ ปลอดภัยกว่าเผลอ build ขึ้น hosting จริงแล้วชี้ dev */
export const DEFAULT_API_BASE = API_ENDPOINTS.prod;

/**
 * report service เป็นคนละตัวกับ core API — เป็น Express บน DigitalOcean
 * ไม่ใช่ API Gateway จึงห้ามใช้ค่าเดียวกับ API_ENDPOINTS
 *
 * ตั้ง NUXT_PUBLIC_REPORT_API_BASE เพื่อชี้ไปที่อื่น เช่นรัน server บนเครื่อง
 * ตอนทดสอบ (`NUXT_PUBLIC_REPORT_API_BASE=http://localhost:8080 npm run dev`)
 */
export const REPORT_API_ENDPOINTS = {
  prod: "https://seal-app-xnngl.ondigitalocean.app",
  local: "https://seal-app-xnngl.ondigitalocean.app",
  // local: "http://localhost:8080",
} as const;

export const DEFAULT_REPORT_API_BASE = REPORT_API_ENDPOINTS.prod;

/**
 * กติกาที่ตกลงกันไว้:
 *   - รันบนเครื่อง (nuxt dev)         → dev API
 *   - build ขึ้น cloud (beta / prod)  → production API
 *
 * ทั้งสองกรณีถ้าตั้ง NUXT_PUBLIC_API_BASE ไว้ ค่านั้นชนะเสมอ
 */
export const resolveApiBase = (env: Record<string, any> = {}) => {
  if (env.NUXT_PUBLIC_API_BASE) return env.NUXT_PUBLIC_API_BASE as string;
  return env.NODE_ENV === "development" ? API_ENDPOINTS.dev : DEFAULT_API_BASE;
};

/**
 * เส้น report ต่างจากเส้น POS ตรงที่ไม่มีแยก dev/prod — มี server ตัวเดียวคือ production
 * ดังนั้นทั้ง `nuxt dev` และ build ขึ้น cloud ใช้ production เหมือนกันหมด
 * จะเปลี่ยนได้ทางเดียวคือตั้ง NUXT_PUBLIC_REPORT_API_BASE เอง
 *
 * คืน "" = ไม่มี override ให้ useBaseUrl("report") ตกไปใช้ DEFAULT_REPORT_API_BASE
 */
export const resolveReportApiBase = (env: Record<string, any> = {}) => {
  return usableOverride(
    "NUXT_PUBLIC_REPORT_API_BASE",
    env.NUXT_PUBLIC_REPORT_API_BASE,
    env
  );
};

/**
 * endpoint ที่ชี้กลับมาที่เครื่องตัวเอง — ใช้ได้เฉพาะตอนรัน dev บนเครื่อง
 * ถ้าหลุดเข้าไปใน build ที่ deploy ขึ้น hosting เครื่องลูกค้าจะยิงไป localhost ของตัวเอง
 */
const LOCAL_ENDPOINT_PATTERN =
  /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(:|\/|$)/i;

export const isLocalEndpoint = (url?: string | null) =>
  LOCAL_ENDPOINT_PATTERN.test((url || "").trim());

/**
 * ใช้กับเส้น report เท่านั้น — เส้น POS ไม่ได้ผ่านฟังก์ชันนี้
 *
 * ไม่ตั้ง env = คืน "" ให้ผู้เรียกไปเลือก default เอง
 *
 * ค่าที่ชี้กลับมาที่เครื่องตัวเอง (localhost) มีผลเฉพาะตอน `nuxt dev`
 * build ขึ้น cloud ไม่มีเหตุผลใดที่จะยิง localhost — ใช้ report service ตัวจริงเสมอ
 * (แอปเป็น static build ค่าถูกฝังตอน build ถ้าหลุดไปทุกเครื่องจะยิง localhost ของตัวเอง)
 *
 * เตือนใน log ไว้ด้วย จะได้ไม่งงว่าทำไมค่าที่ตั้งใน .env ไม่มีผลตอน build
 */
const usableOverride = (
  name: string,
  value: string | undefined,
  env: Record<string, any>
) => {
  if (!value) return "";
  if (!isLocalEndpoint(value)) return value;
  if (env.NODE_ENV === "development") return value;

  console.warn(
    `[config] ข้าม ${name}=${value} เพราะชี้ไปที่เครื่องตัวเอง แต่กำลัง build แบบ production — ใช้ค่า production แทน`
  );

  return "";
};

/**
 * Cloudflare Images — ที่เก็บรูปเมนูตัวใหม่ (ใช้คู่กับ S3 ไม่ได้แทนที่)
 *
 * ค่า default ว่างไว้ ตั้งทับด้วย env ก่อน build (แอปเป็น static build ค่าถูกฝังตอน build):
 *   NUXT_PUBLIC_CF_IMAGES_DELIVERY_URL=https://imagedelivery.net/<account-hash>
 *
 * ว่าง = ประกอบ url ไม่ได้ buildCfImageUrl คืน "" แล้วตกไปใช้ S3 ตามเดิม (ดู useCfImageUrl)
 *
 * ส่วน path ที่ใช้ขอ upload ticket เป็นค่าคงที่ CF_IMAGES_UPLOAD_PATH ข้างล่าง
 * ไม่ได้มาจาก env เพราะเป็น route ตายตัวบน core API ของเราเอง
 *
 * หมายเหตุสำคัญเรื่องความปลอดภัย:
 *   API token ของ Cloudflare ห้ามอยู่ฝั่ง browser เด็ดขาด (ต่างจาก key ของ S3 ที่โปรเจกต์นี้
 *   ฝังไว้ในโค้ดมาแต่เดิม) flow ที่ใช้คือ direct upload — ขอ one-time URL จาก backend
 *   แล้ว browser ค่อย POST ไฟล์ไปที่ URL นั้นตรง ๆ backend เป็นคนถือ token
 */
export const CLOUDFLARE_IMAGES = {
  /** base ของ delivery URL: https://imagedelivery.net/<account-hash> — ว่าง = ตกไปใช้ S3 */
  deliveryUrl: "",
} as const;

/**
 * path บน core API ที่ออก "ตั๋วใบเดียวใช้ครั้งเดียว" ให้ browser เอาไปอัปรูปขึ้น Cloudflare
 * (route อยู่ที่ general/routes/products.js ฝั่ง microservices)
 *
 * ต่อท้าย useBaseUrl() เหมือน path อื่น ๆ ใน dataservice
 * backend เป็นคนถือ API token ฝั่ง browser ไม่เคยเห็น token เลย
 */
export const CF_IMAGES_UPLOAD_PATH = "/general/products/upload";

/**
 * path ของ Cloudflare R2 (ตัวใหม่ที่มาแทน Cloudflare Images) — route อยู่ไฟล์เดียวกันฝั่ง microservices
 * uploadR2 คืน presigned PUT url, deleteR2 ลบ object  secret ของ R2 อยู่ที่ backend เท่านั้น
 * key ที่ได้เก็บใน field `cfIdR2` ของสินค้า (ช่วงทดสอบ) ดู useUploadR2
 */
export const R2_UPLOAD_PATH = "/general/products/uploadR2";
export const R2_DELETE_PATH = "/general/products/deleteR2";

/**
 * โดเมน public ของ Cloudflare R2 — ตัวใหม่ที่มาแทน imagedelivery.net ของ Cloudflare Images
 *
 * โดเมนนี้ map กับ bucket R2 (xtable) ไว้แล้ว โครงสร้าง key = assets/<storeId>/products/..
 * ตอนแสดงผลย่อขนาดด้วย Cloudflare Image Transformations ผ่าน path สั้น `<cdnUrl>/i/<size>/<cfIdR2>`
 * (zone มี Transform Rule rewrite /i/<size>/.. -> /cdn-cgi/image/width=..,height=..,fit=cover,format=auto/..)
 * R2 ไม่ย่อในตัวเอง ต้องเปิด Transformations + ตั้ง Transform Rule บน zone ก่อน ดู useCfImageUrl / docs
 *
 * ตั้ง NUXT_PUBLIC_R2_CDN_URL เพื่อ override (แอปเป็น static build ค่าถูกฝังตอน build)
 * ฝั่งแอปอ่านผ่าน useCfImageUrl() ห้ามอ่าน runtimeConfig ตรง ๆ
 */
export const R2_PUBLIC = {
  /** โดเมนใหม่ที่ mapping กับ bucket R2 เรียบร้อยแล้ว */
  cdnUrl: "https://xtable-cdn.co",
  // --- ค่าเดิมเก็บไว้เผื่อฉุกเฉิน (ย้อนกลับเร็ว ๆ) ปลดคอมเมนต์แล้วลบบรรทัดบนออก ---
  // cdnUrl: "",
} as const;

/**
 * ตั้ง env ไว้ = ชนะค่าใน R2_PUBLIC เสมอ (รูปแบบเดียวกับ resolveCloudflareImages)
 * ว่าง = ประกอบ url ไม่ได้ → useCfImageUrl ตกไปใช้ S3
 */
export const resolveR2Public = (env: Record<string, any> = {}) => ({
  cdnUrl: (env.NUXT_PUBLIC_R2_CDN_URL || R2_PUBLIC.cdnUrl).replace(/\/+$/, ""),
});

/**
 * ชื่อ variant ที่ไปตั้งไว้ในหน้า Cloudflare Images > Variants
 * Cloudflare จะ resize ให้ตามชื่อ เราแค่ต่อท้าย delivery URL ตอนแสดงผล
 * ฝั่งแอปห้ามใช้ string ตรง ๆ ให้อ้างผ่าน CF_IMAGE_VARIANT เท่านั้น จะได้แก้ที่เดียว
 *
 * ชื่อ variant = ความกว้างเป็น px ตรง ๆ (ทุกตัวเป็นสี่เหลี่ยมจัตุรัส)
 * เมื่อก่อนเป็น xs / sm / public ซึ่งไม่บอกขนาด เลยต้องมีชั้นแปลงชื่อคั่นอีกที
 * ตอนนี้ชื่อบอกขนาดอยู่แล้ว ชั้นนั้นจึงถูกตัดทิ้ง เหลือชื่อเดียวต่อขนาด
 *
 * เลือกขนาดจาก "พื้นที่จริงบนจอ x DPR" ไม่ใช่จากความสวย
 * รูป 48-64px บนจอ 2x ต้องการแค่ 240 การขอ 1080 มาย่อ = เปลืองเน็ตฟรี ๆ
 *
 * *** ชื่อฝั่งขวาต้องมีอยู่จริงบน Cloudflare ไม่งั้น url จะ 404 ***
 * ตรวจของจริงได้ที่ GET /accounts/<id>/images/v1/variants
 * ตอนตรวจล่าสุด (2026-09-17) account มี: 240, 360, 480, 720, 1080
 */
export const CF_IMAGE_VARIANT = {
  /** รูปเล็กในลิสต์ / ปุ่มเมนู / บรรทัดบิล -- ใช้ตัวนี้เป็นหลัก */
  "240": "240",
  "360": "360",
  /** หน้าแก้ไขสินค้า / QR menu */
  "480": "480",
  "720": "720",
  /** เต็มขนาด ใช้เมื่อต้องโชว์รูปใหญ่เต็มจอจริง ๆ */
  "1080": "1080",
} as const;

export type CfImageVariant = keyof typeof CF_IMAGE_VARIANT;

/**
 * variant ที่ใช้เมื่อผู้เรียกไม่ได้ระบุ
 * ตั้งเป็นตัวเล็กสุดโดยตั้งใจ -- ลืมระบุแล้วได้รูปเล็กไป ยังดีกว่าลืมแล้วโหลด 1080 ทุกที่
 */
export const CF_IMAGE_VARIANT_DEFAULT: CfImageVariant = "240";

/**
 * ตั้ง env ไว้ = ชนะค่าใน CLOUDFLARE_IMAGES เสมอ (รูปแบบเดียวกับ resolveApiBase)
 * ไม่ตั้ง = ได้ค่าว่าง แปลว่าประกอบ delivery url ไม่ได้ → รูปตกไปใช้ S3
 */
export const resolveCloudflareImages = (env: Record<string, any> = {}) => ({
  deliveryUrl: (
    env.NUXT_PUBLIC_CF_IMAGES_DELIVERY_URL || CLOUDFLARE_IMAGES.deliveryUrl
  ).replace(/\/+$/, ""),
});

/**
 * โดเมนหลัก (xtable.co) กับโดเมน beta (app2.xtable.co) ที่ใช้ส่งร้าน beta ไปมา
 * (ดู modules/core/utils/authHandoff.ts) ฝั่งแอปอ่านผ่าน authHandoff.ts เท่านั้น
 *
 * ไม่ตั้ง env = ค่าจริงของ production เสมอ
 * ตั้ง NUXT_PUBLIC_MAIN_ORIGIN / NUXT_PUBLIC_BETA_ORIGIN ได้เพื่อทดสอบบน firebase preview
 * channel (*.web.app) โดยไม่แตะเว็บจริง — ใช้ผ่าน `npm run handoff-test:deploy` เท่านั้น
 * (ไฟล์ .env.handoff-test) ห้ามใส่ใน .env ปกติ เพราะ .env ถูก build ขึ้น production ด้วย
 */
export const SITE_ORIGINS = {
  main: "https://xtable.co",
  beta: "https://app2.xtable.co",
} as const;

// ตั้งมาแต่ไม่ใช่ URL = ให้ build พังไปเลย ดีกว่าเงียบแล้วไปใช้โดเมนจริงตอนทดสอบ
const originOrDefault = (name: string, value: string | undefined, fallback: string) => {
  if (!value) return fallback;
  try {
    return new URL(value).origin;
  } catch {
    throw new Error(`[config] ${name}=${value} ไม่ใช่ URL ที่ใช้ได้`);
  }
};

export const resolveSiteOrigins = (env: Record<string, any> = {}) => ({
  main: originOrDefault("NUXT_PUBLIC_MAIN_ORIGIN", env.NUXT_PUBLIC_MAIN_ORIGIN, SITE_ORIGINS.main),
  beta: originOrDefault("NUXT_PUBLIC_BETA_ORIGIN", env.NUXT_PUBLIC_BETA_ORIGIN, SITE_ORIGINS.beta),
});
